/**
 * Shared session strip: ASR, Start Session, live session id, End Visit.
 * Shown under main app tabs on every workflow screen except Dashboard.
 * Uses the live `session` only so Start Session stays available while editing a past visit (formSessionOverride).
 */
import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
  Chip,
  Tooltip,
  Alert,
} from '@mui/material';
import { useSession } from '../../contexts/SessionContext';
import { useUser } from '../../contexts/UserContext';
import { useTranscript } from '../../contexts/TranscriptContext';
import { useAmbientListening } from '../../contexts/AmbientListeningContext';
import { useFormData } from '../../contexts/FormDataContext';
import { useLabReportUpload } from '../../contexts/LabReportUploadContext';
import { AudioSourceType } from '../Consultation/AudioSourceDialog';
import CabinSelectorDialog from '../Consultation/CabinSelectorDialog';
import SyncStatusIndicator from '../Consultation/SyncStatusIndicator';

export default function WorkflowSessionBar() {
  const { session, formSessionOverride, createSession, completeSession, loading, endFormSessionEdit } = useSession();
  const { patient } = useUser();
  const { isListening, isMicPaused, clearTranscripts } = useTranscript();
  const { state: ambientState, stopListening: stopMic } = useAmbientListening();
  const { clearFormForTab, saveFormData } = useFormData();
  const { clearFiles: clearLabReportFiles } = useLabReportUpload();
  const [error, setError] = useState<string | null>(null);
  const [isEnding, setIsEnding] = useState(false);

  const [cabinSelectorOpen, setCabinSelectorOpen] = useState(false);

  const createSessionWithAudio = async (audioSource: AudioSourceType, cabinId?: string) => {
    try {
      // If we were editing a past visit (formSessionOverride), starting a new session should reset to
      // a fresh visit so old values do not leak into Pre-Consultation / Patient Visit / Management / Billing.
      if (formSessionOverride) {
        endFormSessionEdit();
        clearFormForTab('PreConsultation');
        clearFormForTab('Consultation');
        clearFormForTab('Prescription');
        clearFormForTab('Billing');
      }
      // ASR selection is config/system-driven; no per-session override from UI.
      clearLabReportFiles();
      await createSession(audioSource, cabinId);
      setError(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to start session');
    }
  };

  // Keep a ref to the latest createSessionWithAudio so the event listener below ([] deps,
  // registered once at mount) always calls the current version and never holds a stale
  // closure over an old `patient` value from SessionContext.
  // createSessionWithAudio must be declared ABOVE this line to avoid temporal dead zone.
  const createSessionWithAudioRef = useRef(createSessionWithAudio);
  useEffect(() => { createSessionWithAudioRef.current = createSessionWithAudio; });

  // Listen for audio source selection coming from App.tsx dialog
  useEffect(() => {
    const handler = (e: Event) => {
      const audioSource = (e as CustomEvent).detail.audioSource as AudioSourceType;
      if (audioSource === 'hardware') {
        setCabinSelectorOpen(true);
      } else {
        void createSessionWithAudioRef.current(audioSource);
      }
    };
    window.addEventListener('create-session-with-audio', handler);
    return () => window.removeEventListener('create-session-with-audio', handler);
  }, []);

  const handleStartSession = () => {
    window.dispatchEvent(new CustomEvent('open-audio-source-dialog'));
  };

  const handleCabinSelect = async (cabinId: string) => {
    setCabinSelectorOpen(false);
    await createSessionWithAudio('hardware', cabinId);
  };

  const handleEndSession = async () => {
    setIsEnding(true);
    try {
      // Stop mic first → sends empty blob to backend WebSocket, which triggers
      // final LLM extraction, run_full_transcript_reconciliation, and (Sarvam)
      // batch diarization — all async on the backend, non-blocking here.
      if (ambientState === 'listening' || ambientState === 'processing') {
        try {
          stopMic();
        } catch (micErr) {
          // Non-fatal: visit still ends if mic stop throws (e.g. WS already closed).
          console.warn('[WorkflowSessionBar] stopMic threw during End Visit:', micErr);
        }
      }

      const saveResult = await saveFormData();
      if (!saveResult.success) {
        throw new Error(saveResult.error || 'Could not save forms before ending visit');
      }
      await completeSession();
      clearTranscripts();
      clearLabReportFiles();
      window.dispatchEvent(new CustomEvent('previous-visits:refresh'));
      window.dispatchEvent(new CustomEvent('right-panel:show-summary'));
      setError(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to end session');
    } finally {
      setIsEnding(false);
    }
  };

  return (
    <>
      <Paper elevation={0} sx={{ p: 1.5, bgcolor: '#fafafa', borderRadius: 0, width: '100%' }}>
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 1, py: 0.25 }}>
            {error}
          </Alert>
        )}
        {!session ? (
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { sm: 'center' }, gap: 1.5 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleStartSession}
              disabled={loading || !patient}
              size="small"
              sx={{ fontSize: '0.8rem', py: 1, bgcolor: '#012676', '&:hover': { bgcolor: '#011a5c' }, whiteSpace: 'nowrap' }}
            >
              {patient ? 'Start Visit' : 'Select a patient from Dashboard'}
            </Button>
            {formSessionOverride && (
              <Chip label="Editing past visit (forms)" size="small" sx={{ fontSize: '0.75rem', bgcolor: '#fff3e0' }} />
            )}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Tooltip title={session.session_id} arrow>
                <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220 }}>
                  Session: {session.session_id.substring(0, 36)}…
                </Typography>
              </Tooltip>
              <Chip label={session.status} size="small" color={session.status === 'active' ? 'success' : 'default'} sx={{ fontSize: '0.75rem' }} />
              {formSessionOverride && (
                <Chip label="Forms: past visit" size="small" sx={{ fontSize: '0.75rem', bgcolor: '#fff3e0' }} />
              )}
              <SyncStatusIndicator sessionId={session.session_id ?? null} compact />
              <Box sx={{ flexGrow: 1 }} />
              <Button
                variant="contained"
                color="secondary"
                size="small"
                onClick={handleEndSession}
                disabled={loading || isEnding || session.status === 'completed'}
                startIcon={isEnding ? <CircularProgress size={14} color="inherit" /> : undefined}
                sx={{ fontSize: '0.75rem' }}
              >
                {isEnding ? 'Ending...' : 'End Visit'}
              </Button>
            </Box>
            {isListening && (
              <Typography variant="caption" sx={{ color: isMicPaused ? '#f57c00' : '#4caf50', fontWeight: 500 }}>
                {isMicPaused ? 'Mic Paused' : 'Listening…'}
              </Typography>
            )}
          </Box>
        )}
      </Paper>

      <CabinSelectorDialog
        open={cabinSelectorOpen}
        onClose={() => setCabinSelectorOpen(false)}
        onSelect={handleCabinSelect}
      />
    </>
  );
}
